// 🖊️ MODAL DE ASSINATURA DIGITAL — B212 Fatia 4 (AI_126)
// ─────────────────────────────────────────────────────────────
// Dirige a máquina de estados da assinatura (o orquestrarAssinatura.ts faz a
// rede). Fluxo: gera o PDF (F2) → pede o link do BIRD ID (F1) → abre o link de
// autorização + contagem regressiva → o médico autoriza no BIRD ID → assina (F3+broker) →
// oferece o PDF assinado para download. Impressão (window.print) fica intacta.
// ─────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Loader2,
  CheckCircle2,
  AlertTriangle,
  X,
  Download,
  ExternalLink,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { theme } from "../../../shared/styles/theme";
import { DadosAssinaturaWidget } from "../services/assinaturaCliente";
import {
  gerarPdfDoDocumento,
  solicitarLinkBirdId,
  consultarStatusAssinatura,
  assinarDocumento,
  baixarPdfBase64,
} from "../services/orquestrarAssinatura";
import {
  obterSessaoValida,
  guardarSessao,
  consumirUmaAssinatura,
  limparSessao,
  guardarLinkPendente,
  obterLinkPendente,
  limparLinkPendente,
} from "../services/cofreSessaoAssinatura";
import { useAuth } from "../../auth/context/AuthContext";
import { db } from "../../../lib/firebase";
import {
  addDoc,
  collection,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  hashSegundoFator,
  normalizarCodigo,
  ultimos4Telefone,
  type FatorValidacao,
} from "../../validacoes/services/validacaoCliente";
import { enviarDocumentoPaciente } from "../services/enviarDocumentoCliente";
import { classificarErroAssinatura } from "../services/classificarErroAssinatura";

// ~15 min = vida da sessão PSC do BIRD ID (bryClient lifetime 900s).
const SEGUNDOS_SESSAO = 900;
const INTERVALO_POLL_MS = 2500;

// B340 (AI_150) — teto do PDF assinado guardado em `prescricoes/{id}/assinaturas`.
// O Firestore limita cada DOCUMENTO a 1 MiB (1.048.576 bytes) e o que conta é a
// STRING base64 (ASCII, 1 byte por caractere), não o binário. Deixamos ~18 KB de
// margem para os demais campos do documento e o overhead do Firestore.
// ⚠️ Comparar contra `pdfBase64.length`, NUNCA contra o tamanho binário.
const LIMITE_BASE64_FIRESTORE = 1_030_000;

// B309/M4 (AI_140) — a authUrl só pode ser https e de um host esperado
// (BRy/BirdID/Soluti). Espelha a allowlist do servidor (assinar.js). Defesa em
// profundidade contra open-redirect caso a resposta da BRy seja adulterada.
const AUTHURL_HOSTS_OK = ["bry.com.br", "birdid.com.br", "soluti.com.br"];
function authUrlHostSeguro(u: string): boolean {
  try {
    const url = new URL(String(u || ""));
    if (url.protocol !== "https:") return false;
    const host = url.hostname.toLowerCase();
    return AUTHURL_HOSTS_OK.some((d) => host === d || host.endsWith("." + d));
  } catch {
    return false;
  }
}

type Fase = "gerando-pdf" | "gerando-link" | "autorizar" | "assinando" | "sucesso" | "erro";

interface ModalAssinaturaProps {
  aberto: boolean;
  onFechar: () => void;
  /** HTML do documento a assinar (mesmo da impressão). */
  html: string;
  /** Rótulo do tipo (ex.: "receituario", "receituario-controle"). */
  tipo: string;
  /** ID da prescrição já salva (o broker valida criadoPor==uid por ele). */
  prescricaoId: string;
  /** Metadados do widget de assinatura (nome/cidade aparecem no PDF). */
  dados: DadosAssinaturaWidget;
  /** Nome do arquivo assinado para download. */
  nomeArquivo?: string;
  /** B290 — código do QR de validação impresso no PDF (canônico, sem hífen). */
  codigoValidacao?: string;
  /** B290 — metadados públicos da validação (sem conteúdo clínico) + 2º fator. */
  dadosValidacao?: {
    medicoNome: string;
    registro: string;
    /** B292 — 2º fator primário: 4 últimos dígitos do celular do paciente. */
    pacienteTelefone: string;
    /** Fallback do 2º fator (B290) quando não há celular válido cadastrado. */
    pacienteNascimento: string;
  };
}

function mensagemErro(e: unknown): string {
  const m = e instanceof Error ? e.message : String(e);
  // Mensagens já são LGPD-safe na origem (backend redige); evita "[object Object]".
  return m || "Falha ao assinar. Tente novamente.";
}

// B332 (AI_149) — o médico não é programador: a tela mostra uma frase clara e o
// detalhe técnico (formato "[F4.assinar] HTTP 502: …") fica só no console.
function mensagemParaOMedico(tecnica: string): string {
  const classe = classificarErroAssinatura(tecnica);
  if (classe === "em-andamento") {
    return "Já existe uma assinatura deste documento em andamento. Aguarde alguns segundos e tente de novo.";
  }
  if (classe === "sessao-expirada") {
    return "A autorização do BIRD ID expirou. Reautorize para assinar.";
  }
  if (/http 5\d\d/i.test(tecnica)) {
    return "O serviço de assinatura (BRy) não respondeu agora. Isso costuma ser passageiro — tente de novo em instantes.";
  }
  if (/failed to fetch|networkerror|load failed/i.test(tecnica)) {
    return "A conexão falhou no meio da assinatura. Confira a internet e tente de novo.";
  }
  return "Não foi possível concluir a assinatura agora. Tente de novo.";
}

function formatarMMSS(seg: number): string {
  const m = Math.floor(seg / 60);
  const s = seg % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const ModalAssinatura: React.FC<ModalAssinaturaProps> = ({
  aberto,
  onFechar,
  html,
  tipo,
  prescricaoId,
  dados,
  nomeArquivo = "documento-assinado",
  codigoValidacao,
  dadosValidacao,
}) => {
  const { usuarioLogado } = useAuth();
  const [fase, setFase] = useState<Fase>("gerando-pdf");
  const [erro, setErro] = useState("");
  const [authUrl, setAuthUrl] = useState("");
  const [segundos, setSegundos] = useState(SEGUNDOS_SESSAO);
  const [tentativa, setTentativa] = useState(0);
  // B212 F5 (AI_127) — status da persistência do PDF assinado (artefato legal).
  const [registro, setRegistro] = useState<"" | "salvando" | "ok" | "falha">("");
  // B290 — status do registro público de validação (QR). Torna VISÍVEL a falha (ex.:
  // rule de `validacoes` não publicada) em vez de engolir só no console.
  const [registroQR, setRegistroQR] = useState<"" | "ok" | "falha">("");
  // B292 Fase 1 — envio do documento ao paciente por SMS. `enviarMarcado` = controle
  // (marcado por padrão; o médico desmarca p/ NÃO enviar, ex.: laudo judicial).
  // `intencaoEnvio` = o médico clicou "Baixar e enviar" → dispara o SMS assim que o
  // registro do QR (`registroQR==="ok"`) estiver pronto.
  const [statusEnvio, setStatusEnvio] = useState<"" | "enviando" | "ok" | "falha">("");
  const [erroEnvio, setErroEnvio] = useState("");
  const [canalEnvio, setCanalEnvio] = useState(""); // "whatsapp" | "sms" — canal que enviou
  const [enviarMarcado, setEnviarMarcado] = useState(true);
  const [intencaoEnvio, setIntencaoEnvio] = useState(false);
  // B332 — a falha veio da sessão REAPROVEITADA do cofre? (mostra o escape
  // manual "Reautorizar no BIRD ID" na tela de erro, sem apagar o cofre sozinho)
  const [falhouComCofre, setFalhouComCofre] = useState(false);

  // Elegível ao envio por SMS: tem código de validação + celular do paciente válido.
  // (O envio por SMS é configurado no servidor — COMTELE_API_KEY/ROUTE; se faltar, o
  // endpoint devolve erro e o modal mostra "falha", sem quebrar o fluxo de assinatura.)
  const podeEnviarSms =
    !!codigoValidacao && !!ultimos4Telefone(dadosValidacao?.pacienteTelefone || "");

  const pdfBase64Ref = useRef("");
  // B292 Fase 1 — trava SÍNCRONA contra duplo-clique no envio (statusEnvio é async).
  const enviandoRef = useRef(false);
  const sessaoRef = useRef("");
  const requestIdRef = useRef("");
  const pdfAssinadoRef = useRef("");
  const canceladoRef = useRef(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // B332 — timer do retry de 409 (precisa ser cancelável junto com os demais).
  const esperaRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 🔑 B332 — SENHA DA EXECUÇÃO. `canceladoRef` sozinho não basta: na FILA do caso
  // misto o modal NÃO desmonta (o Receituario só troca as props), então o cleanup
  // marca cancelado=true e o corpo do efeito seguinte o repõe como false no MESMO
  // commit — ressuscitando continuações do documento anterior (que então assinavam
  // com dados misturados). Cada execução do fluxo pega um número; toda continuação
  // confere se ainda é a execução vigente antes de tocar em estado ou rede.
  const execRef = useRef(0);

  /** Gera um requestId NOVO (nunca determinístico: id repetido = replay indevido). */
  const novoRequestId = useCallback((): string => {
    const id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${prescricaoId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    requestIdRef.current = id;
    return id;
  }, [prescricaoId]);

  // requestId estável por DOCUMENTO (idempotência: retry não cobra 2×).
  // ⚠️ B332 (AI_149): prescricaoId nas deps é ESSENCIAL — na fila do caso misto
  // (B306) o modal NÃO desmonta entre os documentos (aberto fica true) e, sem
  // isso, o 2º documento herdava o requestId do 1º → 409 de corrida (que ainda
  // apagava o cofre da sessão) ou, pior, replay do CMS do documento errado.
  useEffect(() => {
    if (aberto) novoRequestId();
  }, [aberto, prescricaoId, novoRequestId]);

  // Fluxo principal (roda ao abrir e a cada "tentar de novo").
  useEffect(() => {
    if (!aberto) return;
    canceladoRef.current = false;
    // B332 — esta execução assume o comando; continuações das anteriores (fila,
    // retry, gravações em voo) passam a se reconhecer como obsoletas e param.
    execRef.current += 1;
    const minhaExec = execRef.current;
    /** Esta continuação ainda vale? (documento trocou / modal fechou / novo retry) */
    const obsoleto = () => canceladoRef.current || execRef.current !== minhaExec;
    setErro("");
    setAuthUrl("");
    setSegundos(SEGUNDOS_SESSAO);
    setRegistro("");
    setRegistroQR("");
    setStatusEnvio("");
    setErroEnvio("");
    setCanalEnvio("");
    setEnviarMarcado(true);
    setIntencaoEnvio(false);
    setFalhouComCofre(false);
    enviandoRef.current = false;
    pdfAssinadoRef.current = "";

    const limparTimers = () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (esperaRef.current) clearTimeout(esperaRef.current); // B332 — retry de 409
      pollRef.current = null;
      countdownRef.current = null;
      esperaRef.current = null;
    };

    // B212 F5 (AI_127) — guarda o PDF assinado (artefato legal re-validável no
    // ITI) numa subcoleção append-only `prescricoes/{id}/assinaturas`, no Firestore
    // do Brasil (nunca no Storage/EUA). Tolerante a falha: nunca interrompe o
    // sucesso nem o download. Guarda de 900 KB contra o limite de 1 MB do doc.
    // B290 — grava o registro público de validação `validacoes/{codigo}` (doc id =
    // código do QR). Guarda só PONTEIRO (prescrição+assinatura) + metadados
    // não-sensíveis (médico/CRM/tipo/data) + HASH do 2º fator. NUNCA conteúdo
    // clínico. Tolerante a falha: o documento segue assinado e baixável mesmo aqui.
    const registrarValidacao = async (assinaturaId: string): Promise<void> => {
      try {
        if (!codigoValidacao || !dadosValidacao) return;
        const codigo = normalizarCodigo(codigoValidacao);
        if (!codigo) return;
        // B292 — 2º fator: 4 últimos dígitos do celular quando há celular válido;
        // senão cai p/ a data de nascimento (rede de segurança p/ cadastro legado
        // sem telefone — não deixa o documento "intravável"). O fator escolhido fica
        // GRAVADO no registro, então a página pública pede o campo certo.
        const tel4 = ultimos4Telefone(dadosValidacao.pacienteTelefone || "");
        const fator: FatorValidacao = tel4 ? "celular4" : "nascimento";
        const valorFator =
          fator === "celular4"
            ? dadosValidacao.pacienteTelefone || ""
            : dadosValidacao.pacienteNascimento || "";
        const fatorHash = await hashSegundoFator(codigo, fator, valorFator);
        await setDoc(doc(db, "validacoes", codigo), {
          prescricaoId,
          assinaturaId,
          medicoNome: dadosValidacao.medicoNome || "",
          registro: dadosValidacao.registro || "",
          tipo,
          assinadoEm: serverTimestamp(),
          fator,
          fatorHash,
          criadoPor: usuarioLogado?.id || "",
          valido: true,
          tentativasErradas: 0,
          bloqueadoAte: 0,
        });
        // B332 — gravação em voo do documento ANTERIOR não pode pintar de "ok" a
        // tela do documento seguinte (na fila isso liberava o envio ao paciente
        // antes do registro deste documento existir → 404 no /enviar-documento).
        if (!obsoleto()) setRegistroQR("ok");
      } catch (e) {
        // Falha mais comum: a rule de `validacoes` não publicada (permission-denied).
        // Antes ficava só no console → o médico não sabia que o QR não validaria.
        console.warn("⚠️ [B290] Falha ao registrar a validação pública:", e);
        if (!obsoleto()) setRegistroQR("falha");
      }
    };

    const persistirAssinatura = async (
      pdfBase64: string,
      requestIdUsado: string
    ): Promise<boolean> => {
      try {
        const bytes = Math.floor((pdfBase64.length * 3) / 4); // base64 → bytes (aprox.)
        // B340 (AI_150) — a guarda estava na UNIDADE ERRADA e deixava passar
        // documento que o Firestore rejeita. O que é gravado é a STRING base64
        // (1,333× o binário), e o teto do Firestore é 1 MiB POR DOCUMENTO:
        // um PDF de 900.000 B vira 1.200.000 chars e estoura. Havia, portanto,
        // uma janela (786.432–900.000 B de PDF) em que a guarda aprovava e o
        // `addDoc` lançava — caindo no catch, sem PDF guardado e sem registro do
        // QR. Agora medimos o que de fato é gravado, com margem p/ os demais
        // campos do documento. Medido na AI_150: um pedido com 3 fichas do SUS
        // já ocupa ~999 mil chars (95% do teto) ANTES da assinatura entrar.
        if (pdfBase64.length > LIMITE_BASE64_FIRESTORE) {
          console.warn("⚠️ [F5] PDF assinado grande demais p/ o Firestore; não persistido.");
          // B340 (AI_150) — sem persistir o PDF não há o que o /validar mostre, então
          // o registro `validacoes/{codigo}` também NÃO acontece: o QR impresso no
          // papel nasce morto. Antes isso ficava só no console e o médico via
          // "Documento assinado!" sem saber (mesma lição do B290). Agora cai no
          // aviso que já existe na tela. Vale mais agora que o QR foi para as
          // fichas do SUS — documento com LME/ficha anexa é o mais pesado.
          if (!obsoleto()) setRegistroQR("falha");
          return false;
        }
        const assinaturaRef = await addDoc(
          collection(db, "prescricoes", prescricaoId, "assinaturas"),
          {
            pdfBase64,
            bytes,
            tipo,
            assinadoPor: usuarioLogado?.id || "",
            assinadoPorNome: usuarioLogado?.nome || "",
            assinadoEm: serverTimestamp(),
            // B332 — o requestId DESTA assinatura (não o ref, que a fila já trocou).
            requestId: requestIdUsado,
          }
        );
        // Flag leve no doc principal (UI/queries); falha aqui não invalida o artefato.
        try {
          await updateDoc(doc(db, "prescricoes", prescricaoId), {
            assinado: true,
            assinadoEm: serverTimestamp(),
          });
        } catch (e2) {
          console.warn("⚠️ [F5] flag 'assinado' não atualizado:", e2);
        }
        // B290 — registro público de validação (QR). Só PONTEIRO + metadados
        // não-sensíveis + HASH do 2º fator; NUNCA conteúdo clínico. Tolerante a
        // falha: nunca interrompe o sucesso da assinatura nem o download.
        await registrarValidacao(assinaturaRef.id);
        return true;
      } catch (e) {
        // LGPD-safe: nunca loga o conteúdo, só o erro técnico.
        console.warn("⚠️ [F5] Falha ao persistir o PDF assinado:", e);
        // B340 (AI_150) — se a persistência falhou, `registrarValidacao` não
        // rodou: o QR impresso no papel não vai validar. O médico precisa saber
        // ANTES de entregar o documento (o aviso já existe na tela; faltava
        // acioná-lo por este caminho).
        if (!obsoleto()) setRegistroQR("falha");
        return false;
      }
    };

    // B332 — trava do retry de 409: aguarda-e-retenta UMA vez por execução do fluxo.
    let aguardou409 = false;

    const assinarAgora = async (vindaDeCofre: boolean) => {
      // B332 — o requestId DESTA tentativa fica preso na closure: se a fila
      // avançar no meio, esta continuação para (obsoleto) em vez de mandar o
      // requestId do documento seguinte com os dados deste.
      const meuRequestId = requestIdRef.current || novoRequestId();
      try {
        setFase("assinando");
        const r = await assinarDocumento({
          pdfBase64: pdfBase64Ref.current,
          sessao: sessaoRef.current,
          prescricaoId,
          requestId: meuRequestId,
          dados,
        });
        if (obsoleto()) return;
        consumirUmaAssinatura(); // desconta 1 do cofre da sessão multi-assinatura
        pdfAssinadoRef.current = r.pdfAssinadoBase64;
        setFase("sucesso");
        // B212 F5 (AI_127) — persiste o PDF assinado em background (não bloqueia).
        setRegistro("salvando");
        persistirAssinatura(r.pdfAssinadoBase64, meuRequestId).then((ok) => {
          if (!obsoleto()) setRegistro(ok ? "ok" : "falha");
        });
      } catch (e) {
        if (obsoleto()) return;
        // B332 (AI_149): o cofre só é descartado quando o SERVIDOR diz que a
        // sessão morreu. Antes, QUALQUER erro (409 de corrida, 500 transitório
        // da BRy, queda de rede) apagava uma sessão de 12h válida e pedia QR.
        const classe = classificarErroAssinatura(mensagemErro(e));
        if (vindaDeCofre && classe === "sessao-expirada") {
          // Sessão realmente inválida na BRy → limpa o cofre e refaz com QR.
          limparSessao();
          novoRequestId();
          setTentativa((t) => t + 1);
          return;
        }
        if (classe === "em-andamento" && !aguardou409) {
          // Já existe uma chamada em curso com o MESMO requestId (duplo disparo
          // raro). Aguarda e re-tenta UMA vez. O servidor NÃO devolve o CMS
          // anterior às cegas: cada tentativa reprepara o PAdES (hora nova no
          // placeholder = hash novo), então ele reassina — 1 crédito a mais é o
          // preço de um documento VÁLIDO (portão 2b em assinaturaServidor.js).
          aguardou409 = true;
          await new Promise((resolve) => {
            esperaRef.current = setTimeout(resolve, 2500);
          });
          esperaRef.current = null;
          if (obsoleto()) return;
          await assinarAgora(vindaDeCofre);
          return;
        }
        // Erro transitório/indeterminado: mostra SEM apagar o cofre — "Tentar de
        // novo" reusa a sessão; "Reautorizar no BIRD ID" (se veio do cofre) é o
        // escape manual para sessão morta que a BRy não sinalizou como expirada.
        console.warn("⚠️ [assinatura] falha:", mensagemErro(e));
        setErro(mensagemParaOMedico(mensagemErro(e)));
        setFalhouComCofre(vindaDeCofre);
        setFase("erro");
      }
    };

    (async () => {
      try {
        setFase("gerando-pdf");
        const pdfGerado = await gerarPdfDoDocumento(html, tipo);
        if (obsoleto()) return; // B332 — não escreve o PDF de um doc já superado
        pdfBase64Ref.current = pdfGerado;

        // ♻️ Sessão já autorizada e válida no cofre (deste médico)? Pula o QR e
        // assina direto — mesmo se o prontuário foi aberto em janela NOVA (o
        // cofre é compartilhado entre as janelas via localStorage).
        const sessaoReuso = obterSessaoValida(usuarioLogado?.id);
        if (sessaoReuso) {
          sessaoRef.current = sessaoReuso;
          await assinarAgora(true);
          return;
        }

        // 1ª assinatura do plantão (ou sessão expirada): pede o link + QR.
        // B343-FU (AI_150) — antes de pedir um link NOVO, reaproveita o pendente
        // se ainda estiver na janela de autorização. Cada psc/link abre uma
        // sessão de 12h no certificado que não temos como revogar; numa pane
        // (04/08, o BirdID devolvendo 500) o médico clica "Tentar de novo"
        // dezenas de vezes e cada clique abria mais uma.
        setFase("gerando-link");
        const pendente = obterLinkPendente(usuarioLogado?.id);
        const novo = pendente || (await solicitarLinkBirdId());
        const { authUrl: url, sessao, lifetime, numberOfDocuments } = novo;
        if (obsoleto()) return;
        if (!pendente) {
          guardarLinkPendente(
            url,
            sessao,
            SEGUNDOS_SESSAO,
            usuarioLogado?.id,
            lifetime,
            numberOfDocuments
          );
        }
        sessaoRef.current = sessao;
        setAuthUrl(url);
        setFase("autorizar");

        // Contagem regressiva da janela de autorização.
        countdownRef.current = setInterval(() => {
          setSegundos((s) => {
            if (s <= 1) {
              limparTimers();
              if (!obsoleto()) {
                setErro("O tempo para autorizar no BIRD ID expirou. Tente de novo.");
                setFase("erro");
              }
              return 0;
            }
            return s - 1;
          });
        }, 1000);

        // Poll do status até o médico autorizar no app.
        pollRef.current = setInterval(async () => {
          try {
            const pronto = await consultarStatusAssinatura(sessaoRef.current);
            if (obsoleto()) return;
            if (pronto) {
              limparTimers();
              // Guarda a sessão autorizada no cofre (amarrada a este médico) →
              // próximas receitas, em qualquer janela, pulam o QR.
              guardarSessao(sessao, lifetime, numberOfDocuments, usuarioLogado?.id || "");
              // B343-FU — autorizada: o link deixa de ser "pendente".
              limparLinkPendente();
              await assinarAgora(false);
            }
          } catch (e) {
            limparTimers();
            // B343 (AI_150) — se a BRy recusou a SESSÃO (410), o cofre de 12h está
            // guardando uma autorização morta: sem apagá-lo, "Tentar de novo"
            // reusaria a mesma sessão e cairia no mesmo laço de poll. Apagar aqui
            // faz a próxima tentativa nascer com QR novo.
            if (classificarErroAssinatura(mensagemErro(e)) === "sessao-expirada") {
              limparSessao();
              // B343-FU — a sessão recusada é a DESTE link: reaproveitá-lo cairia
              // no mesmo erro. Descarta para a próxima tentativa pedir um novo.
              limparLinkPendente();
            }
            if (!obsoleto()) {
              setErro(mensagemParaOMedico(mensagemErro(e)));  // B332: técnico só no console
              setFase("erro");
            }
          }
        }, INTERVALO_POLL_MS);
      } catch (e) {
        if (!obsoleto()) {
          setErro(mensagemParaOMedico(mensagemErro(e)));  // B332: técnico só no console
          setFase("erro");
        }
      }
    })();

    return () => {
      canceladoRef.current = true;
      limparTimers();
    };
    // html/tipo/prescricaoId/dados vêm do estado do componente pai (referências
    // estáveis durante a vida do modal), então só `tentativa` re-dispara o fluxo.
  }, [
    aberto,
    tentativa,
    html,
    tipo,
    prescricaoId,
    dados,
    codigoValidacao,
    dadosValidacao,
    novoRequestId,
  ]);

  // B292 Fase 1 — dispara o SMS quando o médico pediu o envio (clicou "Baixar e
  // enviar") E o registro do QR (`validacoes/{codigo}`) já foi gravado. Assim um
  // clique antes do registro ficar pronto ainda envia, sem chamar o endpoint cedo
  // (404). Re-tentável a partir de "falha"; idempotente após "ok" (+ dedup no servidor).
  useEffect(() => {
    if (
      !intencaoEnvio ||
      registroQR !== "ok" ||
      statusEnvio === "ok" ||
      statusEnvio === "enviando" ||
      enviandoRef.current ||
      !codigoValidacao
    ) {
      return;
    }
    enviandoRef.current = true;
    setIntencaoEnvio(false);
    setStatusEnvio("enviando");
    setErroEnvio("");
    let ativo = true;
    enviarDocumentoPaciente(normalizarCodigo(codigoValidacao))
      .then((r) => {
        if (!ativo) return;
        setStatusEnvio(r.ok ? "ok" : "falha");
        if (r.ok) setCanalEnvio(r.canal || "");
        else setErroEnvio(r.erro || "");
      })
      .catch(() => {
        if (ativo) setStatusEnvio("falha");
      })
      .finally(() => {
        enviandoRef.current = false;
      });
    return () => {
      ativo = false;
    };
  }, [intencaoEnvio, registroQR, statusEnvio, codigoValidacao]);

  if (!aberto) return null;

  // B332 — "Tentar de novo" é uma tentativa NOVA: id novo. Reusar o id anterior
  // criava becos sem saída (claim "pendente" órfão → 409 eterno) e não protegia
  // contra cobrança dupla, porque cada tentativa reprepara o PAdES com a hora
  // corrente → hash diferente → o portão 2b do servidor reassina de qualquer forma.
  const tentarDeNovo = () => {
    novoRequestId();
    setTentativa((t) => t + 1);
  };
  const baixar = () => baixarPdfBase64(pdfAssinadoRef.current, nomeArquivo);

  // B332 — escape manual da tela de erro: o médico decide recomeçar com QR
  // (para sessão do cofre morta de um jeito que a BRy não sinaliza como expirada).
  const reautorizarBirdId = () => {
    limparSessao();
    novoRequestId(); // B332 — id novo: nunca reaproveita um claim já usado
    setTentativa((t) => t + 1);
  };

  // B332 — fechar durante a fase "assinando" descartava em silêncio um documento
  // cuja assinatura já estava sendo consumida na BRy (e, na fila do caso misto
  // B306, ainda avançava para o 2º documento no meio do processo — visto ao vivo
  // em 28/07). O X passa a pedir confirmação nessa fase.
  const fecharComGuarda = () => {
    if (fase === "assinando") {
      const confirmou = window.confirm(
        "A assinatura está em andamento. Se você interromper agora, este documento pode ficar sem o PDF assinado. Interromper mesmo assim?"
      );
      if (!confirmou) return;
    }
    onFechar();
  };

  // B292 Fase 1 — UM botão: baixa o PDF e (se marcado + elegível) envia o SMS. O
  // envio em si dispara no useEffect acima, assim que o registro do QR estiver pronto.
  const baixarEEnviar = () => {
    baixar();
    if (podeEnviarSms && enviarMarcado) setIntencaoEnvio(true);
  };

  const overlay: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    padding: 16,
  };
  const card: React.CSSProperties = {
    background: "white",
    borderRadius: theme.borderRadius.lg,
    maxWidth: 460,
    width: "100%",
    boxShadow: theme.shadows.xl,
    overflow: "hidden",
    // Herda a fonte sans do app (sem isto, o overlay cai no serif do navegador).
    fontFamily: theme.fonts.sans,
  };
  const header: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    background: theme.gradients.header,
    color: "white",
  };
  const corpo: React.CSSProperties = {
    padding: 24,
    textAlign: "center",
    minHeight: 220,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  };
  const botaoPrimario: React.CSSProperties = {
    background: theme.primary[600],
    color: "white",
    border: "none",
    borderRadius: theme.borderRadius.md,
    padding: "10px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontFamily: theme.fonts.sans, // <button> não herda a fonte do card
  };
  const botaoSecundario: React.CSSProperties = {
    ...botaoPrimario,
    background: theme.gray[100],
    color: theme.gray[700],
  };
  const spin: React.CSSProperties = { animation: "spin 1s linear infinite" };

  const tempoBaixo = segundos < 60;

  return (
    // B332: SEM onClick no overlay — clique-fora não fecha (padrão dos modais
    // do app); fechar é só pelo X (com guarda) ou pelos botões.
    <div style={overlay}>
      <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
      <div style={card}>
        <div style={header}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
            <ShieldCheck size={20} /> Assinatura digital
          </span>
          <X size={20} style={{ cursor: "pointer" }} onClick={fecharComGuarda} />
        </div>

        <div style={corpo}>
          {fase === "gerando-pdf" && (
            <>
              <Loader2 size={40} color={theme.primary[600]} style={spin} />
              <p style={{ color: theme.gray[600], margin: 0 }}>Preparando o documento…</p>
            </>
          )}

          {fase === "gerando-link" && (
            <>
              <Loader2 size={40} color={theme.primary[600]} style={spin} />
              <p style={{ color: theme.gray[600], margin: 0 }}>
                Gerando o link de autorização do BIRD ID…
              </p>
            </>
          )}

          {fase === "autorizar" && (
            <>
              <p style={{ margin: 0, fontWeight: 600, color: theme.gray[800] }}>
                Autorize no BIRD ID
              </p>
              {authUrl && (
                <button
                  style={botaoPrimario}
                  onClick={() => {
                    // B309/M4 (AI_140) — defesa em profundidade: só abre a authUrl se
                    // for https e de um host esperado (BRy/BirdID/Soluti). O servidor
                    // já valida (assinar.js), este é o 2º portão antes do window.open.
                    if (!authUrlHostSeguro(authUrl)) {
                      alert(
                        "Link de autorização inválido (host inesperado). Tente novamente."
                      );
                      return;
                    }
                    window.open(authUrl, "_blank", "noopener,noreferrer");
                  }}
                >
                  <ExternalLink size={16} /> Abrir autorização do BIRD ID
                </button>
              )}
              <p style={{ margin: 0, fontSize: 13, color: theme.gray[600] }}>
                Abre a página do BIRD ID para você confirmar. Depois de autorizar,
                esta janela segue sozinha.
              </p>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: tempoBaixo ? theme.status.error.border : theme.primary[700],
                }}
              >
                Expira em {formatarMMSS(segundos)}
              </div>
              <p style={{ margin: 0, fontSize: 11, color: theme.gray[500] }}>
                Aguardando autorização… isto consome 1 assinatura.
              </p>
            </>
          )}

          {fase === "assinando" && (
            <>
              <Loader2 size={40} color={theme.primary[600]} style={spin} />
              <p style={{ color: theme.gray[600], margin: 0 }}>Assinando o documento…</p>
            </>
          )}

          {fase === "sucesso" && (
            <>
              <CheckCircle2 size={48} color={theme.status.success.border} />
              <p style={{ margin: 0, fontWeight: 700, color: theme.status.success.text }}>
                Documento assinado!
              </p>
              <p style={{ margin: 0, fontSize: 13, color: theme.gray[600] }}>
                Assinatura eletrônica qualificada (ICP-Brasil).
              </p>
              {obterSessaoValida(usuarioLogado?.id) && (
                <p style={{ margin: 0, fontSize: 11, color: theme.primary[700], fontWeight: 600 }}>
                  ♻️ Sessão ativa — as próximas receitas assinam sem novo QR.
                </p>
              )}
              {/* B212 F5: status da persistência do registro assinado (legal). */}
              {registro === "salvando" && (
                <p style={{ margin: 0, fontSize: 11, color: theme.gray[600] }}>
                  Guardando o registro…
                </p>
              )}
              {registro === "ok" && (
                <p style={{ margin: 0, fontSize: 11, color: theme.status.success.text, fontWeight: 600 }}>
                  📁 Registro guardado.
                </p>
              )}
              {registro === "falha" && (
                <p style={{ margin: 0, fontSize: 11, color: theme.status.error.text }}>
                  ⚠️ Não foi possível guardar o registro (o download abaixo funciona).
                </p>
              )}
              {/* B290 — avisa se o QR de validação não foi registrado (ex.: rule não
                  publicada). O documento está assinado; só a checagem pela farmácia falha. */}
              {registroQR === "falha" && (
                <p style={{ margin: 0, fontSize: 11, color: theme.status.error.text }}>
                  ⚠️ O QR de validação não foi registrado — o documento está assinado, mas
                  a conferência pela farmácia pode não funcionar.
                </p>
              )}
              {/* B292 Fase 1 — controle: enviar ao paciente por SMS junto com o
                  download (marcado por padrão; desmarque p/ NÃO enviar, ex.: laudo
                  judicial). Indisponível se o registro do QR falhou. */}
              {podeEnviarSms && statusEnvio !== "ok" && registroQR !== "falha" && (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    alignSelf: "stretch",
                    fontSize: 13,
                    color: theme.gray[700],
                    cursor: statusEnvio === "enviando" ? "default" : "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={enviarMarcado}
                    disabled={statusEnvio === "enviando"}
                    onChange={(e) => setEnviarMarcado(e.target.checked)}
                  />
                  <Smartphone size={15} /> Enviar ao paciente (WhatsApp/SMS)
                </label>
              )}

              {/* UM botão: baixa o PDF e (se marcado + elegível) envia o SMS. */}
              <button
                style={botaoPrimario}
                onClick={baixarEEnviar}
                disabled={statusEnvio === "enviando"}
              >
                <Download size={16} />{" "}
                {podeEnviarSms &&
                enviarMarcado &&
                statusEnvio !== "ok" &&
                registroQR !== "falha"
                  ? "Baixar e enviar ao paciente"
                  : "Baixar PDF assinado"}
              </button>

              {/* Status do envio por SMS */}
              {statusEnvio === "enviando" && (
                <p style={{ margin: 0, fontSize: 11, color: theme.gray[600] }}>
                  Enviando ao paciente…
                </p>
              )}
              {intencaoEnvio && statusEnvio === "" && registroQR === "" && (
                <p style={{ margin: 0, fontSize: 11, color: theme.gray[600] }}>
                  Preparando o envio… (a mensagem sai assim que o registro do documento ficar pronto)
                </p>
              )}
              {statusEnvio === "falha" && (
                <p style={{ margin: 0, fontSize: 11, color: theme.status.error.text }}>
                  ⚠️ {erroEnvio || "Não foi possível enviar agora."} Baixe de novo para tentar.
                </p>
              )}
              {statusEnvio === "ok" && (
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    color: theme.status.success.text,
                    fontWeight: 600,
                  }}
                >
                  {canalEnvio === "whatsapp"
                    ? "📲 Enviado ao paciente por WhatsApp."
                    : canalEnvio === "sms"
                    ? "📱 Enviado ao paciente por SMS."
                    : "✅ Enviado ao paciente."}
                </p>
              )}

              <button style={botaoSecundario} onClick={onFechar}>
                Fechar
              </button>
            </>
          )}

          {fase === "erro" && (
            <>
              <AlertTriangle size={44} color={theme.status.error.border} />
              <p style={{ margin: 0, fontWeight: 600, color: theme.status.error.text }}>
                Não foi possível assinar
              </p>
              <p style={{ margin: 0, fontSize: 13, color: theme.gray[600] }}>{erro}</p>
              {falhouComCofre && (
                <p style={{ margin: 0, fontSize: 11, color: theme.gray[500] }}>
                  A sessão de assinatura continua guardada — "Tentar de novo" a
                  reutiliza. Se continuar falhando, reautorize no BIRD ID.
                </p>
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
                <button style={botaoPrimario} onClick={tentarDeNovo}>
                  Tentar de novo
                </button>
                {falhouComCofre && (
                  <button style={botaoSecundario} onClick={reautorizarBirdId}>
                    Reautorizar no BIRD ID
                  </button>
                )}
                <button style={botaoSecundario} onClick={onFechar}>
                  Fechar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalAssinatura;
